import { DateTime as LuxonDateTime } from 'luxon';

export type ActivityStatus = 'success' | 'failed' | 'pending';

/** One row of the Activity feed, precomputed so the template only reads fields. */
export interface ActivityRow {
  id: number;
  run: any;
  agentName: string;
  status: ActivityStatus;
  /** Fixed label, translated by the template. */
  statusLabel: string;
  icon: string;
  /** Text from the run itself (action names, an error message); shown as is. */
  detail: string;
  /** Fixed label, translated by the template, shown after the detail. */
  detailLabel: string;
  time: string;
  /** 'Today' or 'Yesterday' for recent runs, translated by the template; empty otherwise. */
  dayLabel: string;
  triggerName: string;
  triggerIconUrl: string;
  steps: ActivityStep[];
}

/** One action the run executed, in order. */
export interface ActivityStep {
  name: string;
  iconUrl: string;
  failed: boolean;
  error: string;
}

export interface ActivitySummary {
  total: number;
  failed: number;
}

const IN_PROGRESS_STATUSES = ['pending', 'running', 'queued', 'in_progress', 'scheduled', 'waiting', 'created'];

export function toActivityRows(runs: any[]): ActivityRow[] {
  return (runs || []).map(toActivityRow);
}

export function toActivityRow(run: any): ActivityRow {
  const status = toStatus(run?.status);
  const actions: any[] = run?.workflow_version_action_runs || [];
  const time = run?.status_updated_at || run?.created_at || '';
  const detail = toDetail(status, actions);
  return {
    id: run?.id,
    run,
    agentName: run?.workflow_name || '',
    status,
    statusLabel: status === 'success' ? 'Success' : status === 'failed' ? 'Failed' : 'In progress',
    icon: status === 'success' ? 'checkmark-circle' : status === 'failed' ? 'close-circle' : 'time',
    detail: detail.text,
    detailLabel: detail.label,
    time,
    dayLabel: toDayLabel(time),
    triggerName: run?.trigger_name || '',
    triggerIconUrl: run?.trigger_icon_url || '',
    steps: actions.map(toStep),
  };
}

function toStep(action: any): ActivityStep {
  const error = action?.error?.message || '';
  return {
    name: action?.action_name || '',
    iconUrl: action?.action_icon_url || '',
    failed: action?.status === 'failed' || !!error,
    error,
  };
}

export function toActivitySummary(rows: ActivityRow[], totalRecords: number, failedRecords: number | null): ActivitySummary | null {
  if (!rows.length) {
    return null;
  }
  const total = Math.max(totalRecords || 0, rows.length);
  const failed = failedRecords ?? rows.filter(r => r.status === 'failed').length;
  return { total, failed };
}

// A finished run is either a success or a failure; only the states the
// workflow service passes through while working count as in progress.
function toStatus(raw: string): ActivityStatus {
  if (raw === 'success' || raw === 'succeeded') {
    return 'success';
  }
  if (IN_PROGRESS_STATUSES.includes(raw)) {
    return 'pending';
  }
  return 'failed';
}

// The steps list shows what ran; the detail only explains a run with nothing to list.
function toDetail(status: ActivityStatus, actions: any[]): { text: string; label: string } {
  if (actions.length) {
    return { text: '', label: '' };
  }
  if (status === 'failed') {
    return { text: '', label: 'Failed before any action ran' };
  }
  return { text: '', label: status === 'success' ? 'No actions ran' : 'Waiting for actions' };
}

function toDayLabel(value: string): string {
  const dt = value ? LuxonDateTime.fromISO(value).toLocal() : null;
  if (!dt || !dt.isValid) {
    return '';
  }
  const now = LuxonDateTime.local();
  if (dt.hasSame(now, 'day')) {
    return 'Today';
  }
  if (dt.hasSame(now.minus({ days: 1 }), 'day')) {
    return 'Yesterday';
  }
  return '';
}
